import { useEffect, useRef, useCallback, useState } from 'react'

export const useAudioEngine = () => {
  const audioContextRef = useRef(null)
  const audioNodesRef = useRef({
    leftGain: null,
    rightGain: null,
    mainGain: null,
    leftMerger: null,
    rightMerger: null
  })
  const soundPlayingRef = useRef(false)
  const audioBuffersRef = useRef({})
  const [soundsLoaded, setSoundsLoaded] = useState(false)

  const initAudio = useCallback(() => {
    if (audioContextRef.current) return

    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)()
      const nodes = audioNodesRef.current

      // Création des gains pour chaque oreille
      nodes.leftGain = audioContextRef.current.createGain()
      nodes.rightGain = audioContextRef.current.createGain()

      // Création des mergers pour les canaux
      nodes.leftMerger = audioContextRef.current.createChannelMerger(2)
      nodes.rightMerger = audioContextRef.current.createChannelMerger(2)

      // Connexions
      nodes.leftGain.connect(nodes.leftMerger, 0, 0)
      nodes.rightGain.connect(nodes.rightMerger, 0, 1)

      // Gain principal
      nodes.mainGain = audioContextRef.current.createGain()
      nodes.leftMerger.connect(nodes.mainGain)
      nodes.rightMerger.connect(nodes.mainGain)
      nodes.mainGain.connect(audioContextRef.current.destination)

      // Valeurs initiales
      nodes.leftGain.gain.value = 0
      nodes.rightGain.gain.value = 0
      nodes.mainGain.gain.value = 0.5

      // Charger les fichiers audio
      loadAudioFiles()

    } catch (error) {
      console.error('Erreur lors de l\'initialisation de l\'Audio Context:', error)
    }
  }, [])

  const loadAudioFiles = useCallback(async () => {
    if (!audioContextRef.current) return

    const soundFiles = {
      'heartbeat': '/sounds/heartbeatloop-43029.mp3',
      'tibetan-bowl': '/sounds/aud-7-chakra-5-bowl-39233.mp3',
      'drum1': '/sounds/drum1.mp3',
      'drum2': '/sounds/drum2.mp3',
      'drum3': '/sounds/drum3.mp3',
      'piano1': '/sounds/piano1.mp3'
    }

    try {
      const loadPromises = Object.entries(soundFiles).map(async ([key, url]) => {
        try {
          const response = await fetch(url)
          const arrayBuffer = await response.arrayBuffer()
          const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer)
          audioBuffersRef.current[key] = audioBuffer
          console.log(`✅ Son chargé: ${key}`)
        } catch (error) {
          console.error(`❌ Erreur chargement ${key}:`, error)
        }
      })

      await Promise.all(loadPromises)
      setSoundsLoaded(true)
      console.log('🎵 Tous les sons sont chargés')
    } catch (error) {
      console.error('Erreur lors du chargement des sons:', error)
    }
  }, [])

  const playBeep = useCallback((side, soundType, soundFrequency, volume, onJoyConVibration, oscillationFrequency = 0.8) => {
    if (!audioContextRef.current) return

    const nodes = audioNodesRef.current
    
    // Calculer la durée du son en fonction de la fréquence d'oscillation
    // Période = 1000 / freq (en ms), demi-période pour chaque côté
    // On prend 80% de la demi-période pour éviter les chevauchements
    const halfPeriod = (1000 / oscillationFrequency) / 2
    const soundDuration = Math.min(halfPeriod * 0.8, 400) // Max 400ms

    // Vérifier si c'est un son avec fichier audio
    const audioSounds = {
      'heartbeat-real': 'heartbeat',
      'tibetan-bowl': 'tibetan-bowl',
      'drum-1': 'drum1',
      'drum-2': 'drum2',
      'drum-3': 'drum3',
      'piano-1': 'piano1'
    }

    if (audioSounds[soundType] && audioBuffersRef.current[audioSounds[soundType]]) {
      // Jouer le fichier audio avec la durée calculée
      playAudioBuffer(side, audioSounds[soundType], volume, onJoyConVibration, soundDuration)
      return
    }

    // Sinon, utiliser la synthèse audio (code existant)
    soundPlayingRef.current = true
    const osc = audioContextRef.current.createOscillator()

    // Configuration du son selon le type
    switch (soundType) {
      case 'soft-tone':
        osc.type = 'sine'
        osc.frequency.value = 120 + (soundFrequency * 88)
        break
      case 'ocean-wave':
        osc.type = 'triangle'
        osc.frequency.value = 60 + (soundFrequency * 74)
        break
      case 'forest-sound':
        osc.type = 'sine'  
        osc.frequency.value = 180 + (soundFrequency * 100)
        break
      case 'heartbeat':
        osc.type = 'sine'
        osc.frequency.value = 40 + (soundFrequency * 66)
        break
      default:
        osc.type = 'sine'
        osc.frequency.value = 440
    }

    // Routage vers la bonne oreille
    if (side === 'left') {
      osc.connect(nodes.leftGain)
      nodes.leftGain.gain.setValueAtTime(1, audioContextRef.current.currentTime)
      nodes.rightGain.gain.setValueAtTime(0, audioContextRef.current.currentTime)
    } else {
      osc.connect(nodes.rightGain)
      nodes.rightGain.gain.setValueAtTime(1, audioContextRef.current.currentTime)
      nodes.leftGain.gain.setValueAtTime(0, audioContextRef.current.currentTime)
    }

    // Mise à jour du volume principal
    nodes.mainGain.gain.value = volume

    // ✨ SYNC JOY-CON VIBRATION WITH AUDIO ✨
    if (onJoyConVibration) {
      onJoyConVibration(side, soundDuration)
    }

    // Démarrage et arrêt
    osc.start()
    setTimeout(() => {
      osc.stop()
      osc.disconnect()
      setTimeout(() => {
        soundPlayingRef.current = false
      }, 50)
    }, soundDuration)

  }, [])

  const playAudioBuffer = useCallback((side, soundKey, volume, onJoyConVibration, maxDurationMs) => {
    if (!audioContextRef.current || !audioBuffersRef.current[soundKey]) return

    const nodes = audioNodesRef.current
    const source = audioContextRef.current.createBufferSource()
    source.buffer = audioBuffersRef.current[soundKey]

    // Routage vers la bonne oreille
    if (side === 'left') {
      source.connect(nodes.leftGain)
      nodes.leftGain.gain.setValueAtTime(1, audioContextRef.current.currentTime)
      nodes.rightGain.gain.setValueAtTime(0, audioContextRef.current.currentTime)
    } else {
      source.connect(nodes.rightGain)
      nodes.rightGain.gain.setValueAtTime(1, audioContextRef.current.currentTime)
      nodes.leftGain.gain.setValueAtTime(0, audioContextRef.current.currentTime)
    }

    // Mise à jour du volume principal
    nodes.mainGain.gain.value = volume

    // 🎵 Convertir ms en secondes et limiter à la durée calculée
    const maxDurationSec = maxDurationMs / 1000
    const soundDuration = Math.min(source.buffer.duration, maxDurationSec)

    // ✨ SYNC JOY-CON VIBRATION WITH AUDIO ✨
    if (onJoyConVibration) {
      onJoyConVibration(side, soundDuration * 1000)
    }

    // Démarrage du son (on ne lit que le début)
    source.start(0, 0, soundDuration)

    // Arrêt et nettoyage
    setTimeout(() => {
      try {
        source.stop()
      } catch (e) {
        // Le son s'est déjà arrêté
      }
      source.disconnect()
    }, soundDuration * 1000)

  }, [])

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
    }
  }, [])

  return {
    initAudio,
    playBeep,
    soundsLoaded
  }
} 